import React from 'react';
import { IoClose } from "react-icons/io5";
import { FaUserCircle, FaBoxOpen, FaHeart, FaMapMarkerAlt, FaSignOutAlt, FaCog, FaCreditCard } from "react-icons/fa";
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearUserDetails } from '../store/userSlice';
import SummaryApi from '../common';
import { toast } from 'react-toastify';
import ROLE from '../common/role';
import useTranslate from '../hooks/useTranslate';

const ProfileDrawer = ({ onClose, onOpenTrackOrder, onOpenAddresses, onOpenOrders, onOpenPayment, onOpenProfileInfo }) => {
    const user = useSelector(state => state?.user?.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    React.useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const t = useTranslate({
        myOrders: "My Orders",
        updateProfile: "Update Profile",
        savedAddresses: "Saved Addresses",
        accountSettings: "Account Settings",
        myPayment: "My Payment",
        myAccount: "My Account",
        adminDashboard: "Admin Dashboard",
        adminDesc: "Manage products, users, and store settings.",
        verifiedCustomer: "Verified Customer",
        administrator: "Administrator",
        loginAccess: "Login to access your account",
        fasalShopUser: "FasalShop User",
        preferences: "Preferences",
        logout: "Logout",
        closeMenu: "Close Menu"
    });

    const handleLogout = async () => {
        try {
            const fetchData = await fetch(SummaryApi.logout_user.url, {
                method: SummaryApi.logout_user.method,
            });
            const data = await fetchData.json();
            if (data.success) {
                toast.success(data.message);
            } else if (data.error) {
                toast.error(data.message);
            }
            dispatch(clearUserDetails());
            onClose();
            navigate("/");
        } catch (error) {
            console.error(error);
            dispatch(clearUserDetails());
            onClose();
            navigate("/");
        }
    };

    const accountItems = [
        { icon: <FaBoxOpen />, label: t.myOrders, action: () => { onClose(); if (onOpenOrders) onOpenOrders(); } },
        { icon: <FaUserCircle />, label: t.updateProfile, path: "#", action: () => { onClose(); if (onOpenProfileInfo) onOpenProfileInfo(); } },
        { icon: <FaMapMarkerAlt />, label: t.savedAddresses, action: () => { onClose(); if (onOpenAddresses) onOpenAddresses(); } },
        // { icon: <FaCog />, label: t.accountSettings, path: "#", action: () => { onClose(); } }
    ];

    const paymentItems = [
        { icon: <FaCreditCard />, label: t.myPayment, path: "#", action: () => { onClose(); if (onOpenPayment) onOpenPayment(); } }
    ];

    return (
        <div className='fixed top-0 bottom-0 left-0 right-0 z-[100] flex justify-end bg-black/40 backdrop-blur-[2px] transition-all animate-fadeIn'>
            <div className='flex-1' onClick={onClose} aria-hidden="true"></div>

            <div className='bg-white h-full w-full max-w-[400px] shadow-2xl flex flex-col animate-slideInRight overflow-hidden relative transition-colors duration-300'>

                {/* Header Profile Section */}
                <div className='bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] px-6 pt-10 pb-6 shrink-0 relative overflow-hidden'>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -translate-y-8 translate-x-8 mix-blend-screen"></div>

                    <button
                        className='absolute top-4 right-4 text-white/70 hover:text-white transition-all bg-black/10 hover:bg-black/20 p-2 rounded-full backdrop-blur-sm'
                        onClick={onClose}
                    >
                        <IoClose size={20} />
                    </button>

                    <div className='flex items-center gap-5 relative z-10'>
                        <div className='w-16 h-16 rounded-full bg-white/10 border-2 border-[#00b259] flex items-center justify-center text-white shadow-lg backdrop-blur-md overflow-hidden'>
                            {user?.profilePic ? (
                                <img src={user.profilePic} alt={user?.first_name} className='w-full h-full object-cover' />
                            ) : (
                                <FaUserCircle size={40} className="text-[#00b259]/80" />
                            )}
                        </div>
                        <div className="flex flex-col text-white">
                            <h2 className='text-xl justify-center text-white  tracking-wide'>
                                {(user?.name || user?.first_name) ? `${user?.name || user?.first_name} ${user?.last_name || ''}`.trim() : t.fasalShopUser}
                            </h2>
                            <p className='text-xs text-[#00b259] font-bold uppercase tracking-widest mt-0.5'>
                                {user?.role === ROLE.ADMIN ? t.administrator : t.verifiedCustomer}
                            </p>
                            <p className='text-sm text-gray-300 font-medium mt-1 truncate max-w-[200px]'>
                                {user?.email || t.loginAccess}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content Body */}
                <div className='flex-1 overflow-y-auto bg-[#f8fcfb] custom-scrollbar p-6 space-y-6 transition-colors duration-300'>

                    {/* Admin Shortcut */}
                    {user?.role === ROLE.ADMIN && (
                        <div className='bg-indigo-50 border border-indigo-100 rounded-2xl p-5 mb-2 hover:shadow-md transition-shadow relative overflow-hidden group cursor-pointer' onClick={() => { onClose(); navigate("/admin-panel/all-products"); }}>
                            <div className="absolute right-0 top-0 w-16 h-16 bg-indigo-500/10 rounded-bl-full group-hover:scale-125 transition-transform duration-500"></div>
                            <h3 className="text-indigo-900 font-black mb-1 flex items-center gap-2">
                                <span className="bg-indigo-100 text-indigo-600 p-1.5 rounded-lg"><FaCog size={14} /></span> {t.adminDashboard}
                            </h3>
                            <p className="text-indigo-700/70 text-sm font-medium pl-9">{t.adminDesc}</p>
                        </div>
                    )}

                    {/* My Account Section */}
                    <div className='bg-white border border-gray-100 rounded-3xl shadow-sm p-2 flex flex-col transition-colors duration-300'>
                        <h3 className="px-4 pt-4 pb-2 text-[11px] font-black tracking-[3px] text-gray-400 uppercase">{t.myAccount}</h3>
                        {accountItems.map((item, idx) => (
                            <Link
                                key={idx}
                                to={item.path || '#'}
                                onClick={(e) => {
                                    if (item.action) {
                                        e.preventDefault();
                                        item.action();
                                    } else {
                                        onClose();
                                    }
                                }}
                                className={`flex items-center gap-4 px-4 py-4 hover:bg-[#f4fbf7] text-gray-600 hover:text-[#00b259] group transition-colors ${idx !== accountItems.length - 1 ? 'border-b border-gray-50' : ''}`}
                            >
                                <div className='text-xl text-gray-400 group-hover:text-[#00b259] transition-colors'>
                                    {item.icon}
                                </div>
                                <span className='font-bold text-[15px]'>{item.label}</span>
                            </Link>
                        ))}
                    </div>

                    {/* My Payment Section */}
                    <div className='bg-white border border-gray-100 rounded-3xl shadow-sm p-2 flex flex-col transition-colors duration-300'>
                        <h3 className="px-4 pt-4 pb-2 text-[11px] font-black tracking-[3px] text-gray-400 uppercase">{t.myPayment}</h3>
                        {paymentItems.map((item, idx) => (
                            <Link
                                key={idx}
                                to={item.path || '#'}
                                onClick={(e) => {
                                    if (item.action) {
                                        e.preventDefault();
                                        item.action();
                                    } else {
                                        onClose();
                                    }
                                }}
                                className={`flex items-center gap-4 px-4 py-4 hover:bg-[#f4fbf7] text-gray-600 hover:text-[#00b259] group transition-colors ${idx !== paymentItems.length - 1 ? 'border-b border-gray-50' : ''}`}
                            >
                                <div className='text-xl text-gray-400 group-hover:text-[#00b259] transition-colors'>
                                    {item.icon}
                                </div>
                                <span className='font-bold text-[15px]'>{item.label}</span>
                            </Link>
                        ))}
                    </div>

                    {/* Support & Logout */}
                    <div className='bg-white border border-gray-100 rounded-3xl shadow-sm p-2 flex flex-col mt-4 transition-colors duration-300'>
                        <h3 className="px-4 pt-4 pb-2 text-[11px] font-black tracking-[3px] text-gray-400 uppercase">{t.preferences}</h3>

                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-4 px-4 py-4 hover:bg-red-50 text-red-500 group transition-colors text-left w-full rounded-b-xl"
                        >
                            <div className='text-xl text-red-400 group-hover:text-red-500 transition-colors'>
                                <FaSignOutAlt />
                            </div>
                            <span className='font-bold text-[15px]'>{t.logout}</span>
                        </button>
                    </div>

                </div>

                {/* FOOTER CLOSE BUTTON */}
                <div className="p-5 bg-white border-t border-gray-100 mt-auto">
                    <button
                        onClick={onClose}
                        className="w-full bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] hover:brightness-110 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all uppercase tracking-[3px] text-[11px] shadow-xl shadow-green-900/20 active:scale-[0.98]"
                    >
                        {t.closeMenu}
                    </button>
                </div>
            </div>

            {/* Injected animations logic inside tailwind if not present globally, fallback custom css */}
            <style jsx="true">{`
 .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
 .animate-slideInRight { animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
 @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
 @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
 `}</style>

        </div>
    );
};

export default ProfileDrawer;
